import { useState } from "react";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { ExemptPaymentModal } from "./ExemptPaymentModal";
import { useInfo } from "../../hooks/use-info";
import {
  downloadNpePdf,
  downloadReceiptPdf,
} from "@/queries/payment/payment.api";

export interface PaymentActionsProps {
  status: string;
  paymentRequestId: string;
  flowExecutionId?: string;
  isPaymentRequired?: boolean;
  onGenerateReceipt?: () => void;
  onGeneratePaymentOrder?: () => void;
  onExemptPayment?: () => void;
}

export const PaymentActions = ({
  status,
  paymentRequestId,
  flowExecutionId,
  isPaymentRequired = false,
  onGenerateReceipt,
  onGeneratePaymentOrder,
  onExemptPayment,
}: PaymentActionsProps) => {
  const { isCancelled } = useInfo();
  const { showToast } = useToast();
  const [isExemptModalOpen, setIsExemptModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleGenerateReceipt = async () => {
    if (onGenerateReceipt) {
      onGenerateReceipt();
      return;
    }

    try {
      setIsDownloading(true);
      await downloadReceiptPdf(paymentRequestId);
      showToast({
        title: "Éxito",
        description: "El comprobante se descargó correctamente",
        options: { type: "success" },
      });
    } catch (error) {
      console.error("Error al descargar el comprobante:", error);
      showToast({
        title: "Error",
        description:
          "No se pudo descargar el comprobante. Por favor, inténtalo de nuevo.",
        options: { type: "error" },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleGeneratePaymentOrder = async () => {
    if (onGeneratePaymentOrder) {
      onGeneratePaymentOrder();
      return;
    }

    try {
      setIsDownloading(true);
      await downloadNpePdf(paymentRequestId);
      showToast({
        title: "Documento descargado",
        description: "El mandamiento de pago ha sido descargado exitosamente.",
        options: { type: "success" },
      });
    } catch (error) {
      console.error("Error al descargar el mandamiento de pago:", error);
      showToast({
        title: "Error al descargar",
        description:
          "No se pudo descargar el mandamiento de pago. Por favor, inténtalo de nuevo.",
        options: { type: "error" },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExemptPayment = () => {
    if (onExemptPayment) {
      onExemptPayment();
    } else {
      setIsExemptModalOpen(true);
    }
  };

  if (status === "COMPLETED" || status === "PAID") {
    return (
      <section>
        <Button
          variant="tertiary"
          size="lg"
          onClick={handleGenerateReceipt}
          disabled={isCancelled || isDownloading}
        >
          {isDownloading ? "Descargando..." : "Generar comprobante"}
          <Icon type="far" name="file-lines" className="text-current" />
        </Button>
      </section>
    );
  }

  if (status === "PENDING" || status === "REJECTED" || status === "FAILED") {
    return (
      <>
        <section className="flex justify-between gap-3">
          <Button
            variant="tertiary"
            size="lg"
            onClick={handleGeneratePaymentOrder}
            disabled={isCancelled || isDownloading}
          >
            {isDownloading ? "Descargando..." : "Generar mandamiento de pago"}
            <Icon type="far" name="file-lines" className="text-current" />
          </Button>
          {!isPaymentRequired && (
            <Button
              variant="danger-tertiary"
              size="lg"
              onClick={handleExemptPayment}
              disabled={isCancelled}
            >
              Exonerar pago
              <Icon type="far" name="circle-xmark" className="text-current" />
            </Button>
          )}
        </section>

        <ExemptPaymentModal
          isOpen={isExemptModalOpen}
          onClose={() => setIsExemptModalOpen(false)}
          paymentRequestId={paymentRequestId}
          flowExecutionId={flowExecutionId}
        />
      </>
    );
  }

  return null;
};
